import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUserPlus, FaEdit, FaTrash, FaUserShield, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Parse from 'parse';
import { getAllUsers } from '../../services/userService';

/**
 * Componente simplificado para la gestión de usuarios
 * Permite ver, crear, editar y eliminar usuarios
 * Compatible con el sistema de autenticación JWT
 */
const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Cargar usuarios al montar el componente
  useEffect(() => {
    fetchUsers();
    
    // Mostrar mensaje de advertencia sobre la configuración de Back4App
    console.warn(
      'NOTA: Para ver todos los usuarios en Back4App, es necesario configurar ' +
      'los permisos de la clase _User o implementar una Cloud Function con Master Key.'
    );
  }, []);

  // Función para obtener usuarios reales de Back4App
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Obtener usuarios reales de Back4App usando el servicio
      console.log('Obteniendo usuarios reales...');
      const realUsers = await getAllUsers();
      console.log('Usuarios obtenidos de Back4App:', realUsers);
      
      if (!realUsers || realUsers.length === 0) {
        console.warn('No se encontraron usuarios en Back4App');
        setError('No se encontraron usuarios en la base de datos');
        setUsers([]);
        return;
      }
      
      // Transformar los datos al formato esperado por el componente
      const formattedUsers = realUsers.map(user => ({
        id: user.id,
        createdAt: user.createdAt,
        attributes: {
          name: user.name || user.email?.split('@')[0] || 'Usuario',
          email: user.email || 'Sin email',
          username: user.email?.split('@')[0] || user.name || 'usuario',
          type: user.role === 'admin' || user.isAdmin ? 'admin' : 'client',
          emailVerified: user.emailVerified,
          status: user.status || 'active'
        }
      }));
      
      console.log('Usuarios formateados para UI:', formattedUsers);
      setUsers(formattedUsers);
      setError(null);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      setError('No se pudieron cargar los usuarios. Verifica tu conexión e inténtalo de nuevo.');
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar un usuario real de Back4App
  const deleteUser = async (userId) => {
    try {
      // Eliminar usuario de Back4App
      const query = new Parse.Query(Parse.User);
      const user = await query.get(userId);
      
      if (user) {
        // En un entorno de producción, normalmente no se eliminan usuarios
        // sino que se marcan como inactivos
        user.set('status', 'inactive');
        await user.save();
        toast.success('Usuario desactivado correctamente');
      }
      
      // Actualizar la lista de usuarios
      await fetchUsers();
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error al eliminar usuario:', err);
      toast.error('Error al eliminar usuario');
    }
  };

  // Función para convertir un usuario real en administrador en Back4App
  const makeAdmin = async (userId) => {
    try {
      // Obtener el usuario de Back4App
      const query = new Parse.Query(Parse.User);
      const user = await query.get(userId);
      
      if (user) {
        // Actualizar el rol del usuario a administrador
        user.set('role', 'admin');
        user.set('isAdmin', true);
        await user.save();
        toast.success('Usuario convertido en administrador correctamente');
      }
      
      // Actualizar la lista de usuarios
      await fetchUsers();
    } catch (err) {
      console.error('Error al convertir usuario en administrador:', err);
      toast.error('Error al actualizar permisos de usuario');
    }
  };

  // Renderizar tabla de usuarios
  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">Gestión de Usuarios</h1>
        <div className="d-flex gap-2">
          <button 
            className="btn btn-outline-secondary" 
            onClick={fetchUsers} 
            disabled={loading}
          >
            <FaSync className={`me-2 ${loading ? 'fa-spin' : ''}`} />
            Actualizar
          </button>
          <Link to="/admin/users/new" className="btn btn-primary">
            <FaUserPlus className="me-2" />
            Nuevo Usuario
          </Link>
        </div>
      </div>

      {/* Alerta de configuración de Back4App */}
      <div className="alert alert-warning mb-4">
        <h5 className="d-flex align-items-center">
          <FaExclamationTriangle className="me-2" /> 
          Limitación de Back4App
        </h5>
        <p>
          Por razones de seguridad, Back4App no permite que un usuario vea a otros usuarios por defecto. 
          Actualmente hay 4 usuarios en la base de datos, pero solo puedes ver tu propio usuario.
        </p>
        <p className="mb-0">La Cloud Function <code>getAllUsers</code> ya está implementada en el archivo <code>cloud/userFunctions.js</code>, pero necesita ser subida a Back4App:</p>
        <ol className="mt-2 mb-0">
          <li>Ve al Dashboard de Back4App &gt; Servidor &gt; Cloud Code</li>
          <li>Copia el contenido del archivo <code>cloud/userFunctions.js</code> y pégalo en el editor</li>
          <li>Guarda los cambios y despliega el código</li>
          <li>Recarga esta página para ver todos los usuarios</li>
        </ol>
        <div className="mt-3">
          <button 
            className="btn btn-sm btn-primary" 
            onClick={() => fetchUsers()}
            title="Intentar obtener usuarios nuevamente"
          >
            <FaSync className="me-1" /> Reintentar cargar usuarios
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <p>Cargando...</p>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Tipo</th>
                    <th>Fecha de Creación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        {error ? error : 'No hay usuarios registrados'}
                      </td>
                    </tr>
                  ) : (
                    users.map(user => (
                      <tr key={user.id}>
                        <td>{user.attributes?.name || 'Sin nombre'}</td>
                        <td>{user.attributes?.email || user.attributes?.username}</td>
                        <td>
                          <span className={`badge ${user.attributes?.type === 'admin' ? 'bg-danger' : 'bg-primary'}`}>
                            {user.attributes?.type === 'admin' ? 'Administrador' : 'Cliente'}
                          </span>
                          {user.attributes?.emailVerified && (
                            <span className="badge bg-success ms-1">Verificado</span>
                          )}
                        </td>
                        <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="btn-group">
                            <Link to={`/admin/users/edit/${user.id}`} className="btn btn-sm btn-outline-primary">
                              <FaEdit />
                            </Link>
                            {user.attributes?.type !== 'admin' && (
                              <button 
                                className="btn btn-sm btn-outline-warning" 
                                onClick={() => makeAdmin(user.id)}
                                title="Convertir en administrador"
                              >
                                <FaUserShield />
                              </button>
                            )}
                            <button 
                              className="btn btn-sm btn-outline-danger" 
                              onClick={() => setConfirmDelete(user.id)}
                              title="Eliminar usuario"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para eliminar - simplificado sin transiciones */}
      {confirmDelete && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar eliminación</h5>
                <button type="button" className="btn-close" onClick={() => setConfirmDelete(null)}></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro de que deseas desactivar este usuario? El usuario no será eliminado completamente, solo se marcará como inactivo.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancelar</button>
                <button type="button" className="btn btn-danger" onClick={() => deleteUser(confirmDelete)}>Eliminar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
